import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave, FiShield, FiDollarSign, FiCheckCircle, FiAlertCircle, FiLock } = FiIcons;

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative h-8 w-14 rounded-full transition ${checked ? 'bg-primary-600' : 'bg-gray-300'}`}
    aria-pressed={checked}
  >
    <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-7' : 'translate-x-1'}`} />
  </button>
);

const FormsSettings = () => {
  const navigate = useNavigate();
  const [configId, setConfigId] = useState(null);
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  const [siteKey, setSiteKey] = useState('');
  const [academyFee, setAcademyFee] = useState('35');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { data: config, error: configError } = await supabase
        .from('site_config_aox1234')
        .select('id,recaptcha_enabled,recaptcha_site_key,academy_monthly_fee')
        .limit(1)
        .single();

      if (configError) throw configError;

      setConfigId(config.id);
      setRecaptchaEnabled(Boolean(config.recaptcha_enabled));
      setSiteKey(config.recaptcha_site_key || '');
      setAcademyFee(String(config.academy_monthly_fee ?? 35));
    } catch (error) {
      setMessage({ type: 'error', text: `Αποτυχία φόρτωσης: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!configId) return;
    setSaving(true);
    setMessage(null);

    try {
      const parsedFee = Number(academyFee);
      if (!Number.isFinite(parsedFee) || parsedFee < 0) {
        throw new Error('Η μηνιαία συνδρομή πρέπει να είναι έγκυρος αριθμός.');
      }
      if (recaptchaEnabled && !siteKey.trim()) {
        throw new Error('Για ενεργό reCAPTCHA απαιτείται Site Key.');
      }

      const { error: configError } = await supabase
        .from('site_config_aox1234')
        .update({
          recaptcha_enabled: recaptchaEnabled,
          recaptcha_site_key: siteKey.trim(),
          academy_monthly_fee: parsedFee,
          updated_at: new Date().toISOString(),
        })
        .eq('id', configId);

      if (configError) throw configError;

      setMessage({ type: 'success', text: 'Οι ρυθμίσεις φορμών αποθηκεύτηκαν.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Αποτυχία αποθήκευσης.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            >
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-black text-gray-900">Φόρμες & Anti-spam</h1>
              <p className="text-xs text-gray-500">AOX1234 · reCAPTCHA · tracking · Academy fee</p>
            </div>
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center rounded-xl bg-primary-600 px-5 py-2.5 font-bold text-white disabled:opacity-60"
          >
            <SafeIcon icon={FiSave} className="mr-2 h-5 w-5" />
            {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        {message && (
          <div className={`flex items-center rounded-xl border p-4 ${message.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
            <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="mr-2 h-5 w-5" />
            {message.text}
          </div>
        )}

        <section className="rounded-2xl bg-white p-6 shadow-md">
          <div className="mb-6 flex items-center gap-3">
            <SafeIcon icon={FiShield} className="h-6 w-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-black text-gray-900">reCAPTCHA v3</h2>
              <p className="text-sm text-gray-500">Μία AOX ρύθμιση για Contact, Academy Registration και Sponsorship forms.</p>
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between rounded-xl bg-gray-50 p-4">
            <div>
              <div className="font-black text-gray-900">Anti-spam Protection</div>
              <p className="text-sm text-gray-500">Όταν είναι ON, κάθε AOX public form επαληθεύεται server-side πριν αποθηκευτεί.</p>
            </div>
            <Toggle checked={recaptchaEnabled} onChange={setRecaptchaEnabled} />
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <label>
              <span className="mb-1.5 block text-sm font-bold text-gray-800">Site Key</span>
              <input
                className="w-full rounded-xl border px-4 py-3 font-mono text-sm"
                value={siteKey}
                onChange={(e) => setSiteKey(e.target.value)}
              />
            </label>

            <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-900">
              <div className="flex items-center gap-2 font-black">
                <SafeIcon icon={FiLock} className="h-5 w-5" />
                Secret Key: Supabase managed
              </div>
              <p className="mt-2 text-sm leading-relaxed">
                Το server-side Secret Key διαβάζεται από το Supabase Custom Secret RECAPTCHA_SECRET_KEY και δεν αποθηκεύεται ούτε εμφανίζεται στη βάση ή στο browser.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-md">
          <div className="mb-5 flex items-center gap-3">
            <SafeIcon icon={FiDollarSign} className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-xl font-black text-gray-900">Academy</h2>
              <p className="text-sm text-gray-500">Οικονομικές πληροφορίες που εμφανίζονται δυναμικά στη φόρμα εγγραφής.</p>
            </div>
          </div>

          <label className="block max-w-sm">
            <span className="mb-1.5 block text-sm font-bold text-gray-800">Μηνιαία συνδρομή (€)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full rounded-xl border px-4 py-3"
              value={academyFee}
              onChange={(e) => setAcademyFee(e.target.value)}
            />
          </label>
        </section>

        <section className="rounded-2xl border border-blue-200 bg-blue-50 p-6 text-sm text-blue-900">
          <h3 className="font-black">Tracking που αποθηκεύεται αυτόματα</h3>
          <p className="mt-2">
            Κάθε AOX submission κρατά source location, page URL, referrer και UTM source/medium/campaign. Στις χορηγίες παίκτριας κρατάμε επιπλέον player ID και player name ώστε να ξέρουμε ακριβώς για ποια παίκτρια εκδηλώθηκε ενδιαφέρον.
          </p>
        </section>
      </div>
    </div>
  );
};

export default FormsSettings;
